import { makeStyles } from '@material-ui/core';
import PropTypes from 'prop-types';
import React from 'react';
import {
  actionsWidth,
  itemWidth
} from '~/modules/common/charts/timeline/calculations';
import {
  StyledChartRangeItem2,
  STYLED_CHART_RANGE_ITEM_VARIANT
} from '~/modules/common/charts/timeline/components';
import { useResourcingChartToolbarContext } from '~/modules/projects/resourcing-plan/hooks';
import { AllocationRowLoading } from '~/modules/resourcing/common/components/ProjectChartRowsLoading/ProjectChartRowsLoading';
import { useBulkGetTaskResourceUserAllocations } from '~/modules/resourcing/common/hooks';
import { useUserAllocationsSummaryContext } from '~/modules/projects/resourcing-plan/ResourceRequestChart/components/ResourceAllocationChart/components/ResourceAllocationChartRow/UserAllocationsSummaryContext.js';
import { hasOverlap } from '~/modules/resourcing/common/util';
import {
  RowLeftItem,
  RowRightItem,
  TaskAssignmentTimeline
} from './components';

const useStyles = makeStyles(() => ({
  itemContainer: {
    alignItems: 'normal',
    zIndex: 3,
    justifyContent: 'space-between'
  },
  totalContainer: {
    zIndex: 4
  }
}));

const useTimelineStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexBasis: theme.spacing(17.5),
    flexDirection: 'column',
    flexGrow: 1,
    marginTop: theme.spacing(0.5),
    minWidth: theme.spacing(12.5)
  }
}));

export const isTaskAllocationOverDistributed = ({
  userOverDistributedPeriodsMap,
  taskResourceUserAllocation: { startDate, endDate }
}) =>
  Object.values(userOverDistributedPeriodsMap).some(
    ({ periodStartDate, periodEndDate }) =>
      hasOverlap({ start: periodStartDate, end: periodEndDate })({
        dateRange: {
          startDate,
          endDate
        }
      })
  );

const UserTaskAssignmentRow = ({
  loadingActuals,
  taskActualSeriesData,
  taskResourceEstimate,
  userScheduleDetails,
  user,
  chartDisplayPeriods,
  projectId,
  handleRemoveTaskAssignment,
  rowLeftItemClasses,
  loadingUserScheduleDetails,
  backgroundImageOverride
}) => {
  const classes = useStyles();
  const timelineClasses = useTimelineStyles();
  const { scale, chartDates } = useResourcingChartToolbarContext();
  const [{ start: chartStartDate }] = chartDates;

  const { task } = taskResourceEstimate;
  const { id: userUri } = user;
  const { userOverDistributedPeriodsMap } = useUserAllocationsSummaryContext();
  const {
    loading: loadingAllocations,
    taskResourceUserAllocations
  } = useBulkGetTaskResourceUserAllocations({
    userUri,
    projectUri: projectId,
    taskUris: [task.id]
  });

  const hasTaskAllocation = Boolean(taskResourceUserAllocations?.[0]);
  const loading =
    loadingAllocations || loadingActuals || loadingUserScheduleDetails;

  return (
    <StyledChartRangeItem2
      backgroundImageOverride={backgroundImageOverride}
      classes={classes}
      scale={scale}
      scaleItemCount={chartDates.length}
      chartStartDate={chartStartDate}
      leftComponentWidth={actionsWidth + itemWidth * 2}
      variant={STYLED_CHART_RANGE_ITEM_VARIANT.BLANK}
    >
      <RowLeftItem
        taskResourceEstimate={taskResourceEstimate}
        user={user}
        projectId={projectId}
        hasTaskAllocation={hasTaskAllocation}
        handleRemoveTaskAssignment={handleRemoveTaskAssignment}
        isRowOverDistributed={
          hasTaskAllocation
            ? isTaskAllocationOverDistributed({
                userOverDistributedPeriodsMap,
                taskResourceUserAllocation: taskResourceUserAllocations[0]
              })
            : false
        }
        classes={rowLeftItemClasses}
      />
      {loading ? (
        <AllocationRowLoading />
      ) : (
        <div className={timelineClasses.root}>
          <TaskAssignmentTimeline
            chartDisplayPeriods={chartDisplayPeriods}
            handleRemoveTaskAssignment={handleRemoveTaskAssignment}
            user={user}
            userScheduleDetails={userScheduleDetails}
            taskActualSeriesData={taskActualSeriesData}
            taskResourceEstimate={taskResourceEstimate}
            taskResourceUserAllocation={taskResourceUserAllocations[0]}
          />
        </div>
      )}
      <RowRightItem
        totalHours={taskResourceUserAllocations[0]?.totalHours || 0}
        loadingAllocation={loadingAllocations}
      />
    </StyledChartRangeItem2>
  );
};

UserTaskAssignmentRow.propTypes = {
  backgroundImageOverride: PropTypes.string,
  loadingActuals: PropTypes.bool.isRequired,
  taskActualSeriesData: PropTypes.object,
  taskResourceEstimate: PropTypes.object,
  userScheduleDetails: PropTypes.object,
  user: PropTypes.object,
  chartDisplayPeriods: PropTypes.array.isRequired,
  projectId: PropTypes.string.isRequired,
  handleRemoveTaskAssignment: PropTypes.func,
  rowLeftItemClasses: PropTypes.object,
  loadingUserScheduleDetails: PropTypes.bool.isRequired
};

export default UserTaskAssignmentRow;
